import axiosInstance from "../../utils/axiosInstance";
import { GEO_LIFT_MARKET_SELECTION, GEO_LIFT_POWER_PLOT_API } from "../../constants/API";

export interface PowerCalculatorRequest {
  experimentName: string;
  csvFile: File;
  campaignBudget: number;
  bauBudget: number;
  targetCPIC: number;
  statisticalPower: number;
  includeMarket: string;
  treatmentDuration: number;
  effectSizes: string; // comma-separated values like "10,25,50"
  tenantId: string;
}

export interface BestMarket {
  Holdout: number;
  ID: number;
  location: string;
  duration: number;
  EffectSize: number;
  Power: number;
  AvgScaledL2Imbalance: number;
  Investment: number;
  AvgATT: number;
  Average_MDE: number;
  ProportionTotal_Y: number;
  rank: number;
  abs_lift_in_perc: number;
  correlation: number;
  test_markets: string[];
}

export interface PowerCalculatorResponse {
  BestMarkets: [string[], any[]]; // [column names, data rows]
  PowerCurves: [string[], any[]];
  parameters: any;
}

const GeoLiftApiService = {
  /**
   * Calculate power analysis for GeoLift experiment
   * This follows the same pattern as the existing GeoLift measurement flow:
   * 1. Create a job config with GeoLiftJobConfigService
   * 2. Upload CSV and save job config
   * 3. Call market selection API to get results
   *
   * Note: The actual job config creation happens in the component
   * This service just provides the API interface
   */
  async calculatePower(
    jobConfigId: string,
    tenantId: string,
    marketSelectionId: number,
    marketName: string
  ): Promise<any> {
    try {
      const marketSelectionPayload = {
        marketSelectionId,
        marketName,
        jobConfigId,
        jobConfig: jobConfigId,
        tenantId,
      };

      // Call both APIs in parallel like the existing flow does
      const [marketSelectionResult, powerPlotResult] = await Promise.allSettled([
        axiosInstance.post(GEO_LIFT_MARKET_SELECTION, marketSelectionPayload, {
          timeout: 300000, // 5 minutes
        }),
        axiosInstance.post(GEO_LIFT_POWER_PLOT_API, marketSelectionPayload, {
          timeout: 300000, // 5 minutes
        }),
      ]);

      // Check if market selection was successful
      if (marketSelectionResult.status === "rejected") {
        throw new Error("Failed to load market selection data");
      }

      let marketData = marketSelectionResult.value.data;

      // Parse if string
      if (typeof marketData === "string") {
        marketData = JSON.parse(marketData);
      }

      // Return the market data which includes BestMarkets and other results
      return {
        marketSelection: marketData,
        powerPlot: powerPlotResult.status === "fulfilled" ? powerPlotResult.value.data : null,
      };
    } catch (error: any) {
      console.error("Power calculator API error:", error);
      throw new Error(
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        "Failed to calculate power analysis"
      );
    }
  },

  /**
   * Get market selection results for a specific market
   */
  async getMarketSelection(payload: {
    marketSelectionId: number;
    marketName: string;
    jobConfigId: string;
    tenantId: string;
  }) {
    try {
      const response = await axiosInstance.post(GEO_LIFT_MARKET_SELECTION, payload);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to get market selection"
      );
    }
  },

  /**
   * Get power plot data for a specific market
   */
  async getPowerPlot(payload: {
    marketSelectionId: number;
    marketName: string;
    jobConfigId: string;
    tenantId: string;
  }) {
    try {
      const response = await axiosInstance.post(GEO_LIFT_POWER_PLOT_API, payload);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.error || "Failed to get power plot"
      );
    }
  },
};

export default GeoLiftApiService;
